import React, { useState } from 'react';
import config from './config';

const PriorArtDeepDivePage = ({ user, onBack, setPage, onUsageUpdate }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ percent: 0, message: '' });

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    keyFeatures: '',
    field: '',
    priorityDate: ''
  });

  // Results
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [suggestingField, setSuggestingField] = useState(false);
  const [suggestedFields, setSuggestedFields] = useState([]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Suggest technical fields using AI
  const suggestFields = async () => {
    if (!formData.title && !formData.description) {
      setError('Please enter a title or description first');
      return;
    }

    setSuggestingField(true);
    setSuggestedFields([]);

    try {
      const response = await fetch(`${config.API_URL}/api/suggest-field`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description
        })
      });

      const data = await response.json();
      if (data.success && data.fields) {
        setSuggestedFields(data.fields);
      }
    } catch (err) {
      console.error('Error suggesting fields:', err);
    } finally {
      setSuggestingField(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Please fill in at least the title and description');
      return;
    }

    setLoading(true);
    setError('');
    setStep(2);
    setProgress({ percent: 10, message: 'Analyzing your invention...' });

    try {
      const token = localStorage.getItem('token');

      const progressUpdates = [
        { percent: 15, message: 'Extracting key technical concepts...' },
        { percent: 25, message: 'Identifying relevant patent classes...' },
        { percent: 40, message: 'Searching USPTO database (1.45M patents)...' },
        { percent: 55, message: 'Analyzing semantic similarity...' },
        { percent: 70, message: 'Ranking by relevance...' },
        { percent: 85, message: 'Generating detailed analysis...' },
        { percent: 95, message: 'Preparing report...' }
      ];

      let progressIndex = 0;
      const progressInterval = setInterval(() => {
        if (progressIndex < progressUpdates.length) {
          setProgress(progressUpdates[progressIndex]);
          progressIndex++;
        }
      }, 2000);

      const response = await fetch(`${config.API_URL}/api/prior-art-deep-dive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          key_features: formData.keyFeatures,
          field: formData.field,
          priority_date: formData.priorityDate,
          token: token
        })
      });

      clearInterval(progressInterval);

      const data = await response.json();

      if (data.success) {
        setResults(data);
        setStep(3);
        if (onUsageUpdate) onUsageUpdate();
      } else {
        setError(data.error || 'Error performing prior art search');
        setStep(1);
      }
    } catch (err) {
      setError('Error connecting to server. Please try again.');
      setStep(1);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetJourney = () => {
    setStep(1);
    setFormData({ title: '', description: '', keyFeatures: '', field: '', priorityDate: '' });
    setResults(null);
    setError('');
  };

  const getRiskColor = (relevance) => {
    if (relevance >= 80) return { bg: '#fef2f2', border: '#dc2626', text: '#dc2626', label: 'High Relevance' };
    if (relevance >= 50) return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', label: 'Medium Relevance' };
    return { bg: '#dcfce7', border: '#16a34a', text: '#166534', label: 'Lower Relevance' };
  };

  // Save report to database
  const saveReport = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to save reports');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${config.API_URL}/api/saved-individual-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: 'prior-art-deep-dive',
          title: formData?.title || 'Prior Art Deep Dive Report',
          input_data: formData,
          results_data: results,
          token: token
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert(data.error || 'Failed to save report');
      }
    } catch (err) {
      alert('Error saving report');
    } finally {
      setSaving(false);
    }
  };

  // Download as HTML
  const downloadHTML = () => {
    const riskColors = {
      LOW: { bg: '#dcfce7', border: '#16a34a', text: '#166534', icon: '🟢' },
      MEDIUM: { bg: '#fef3c7', border: '#d97706', text: '#92400e', icon: '🟡' },
      HIGH: { bg: '#fee2e2', border: '#dc2626', text: '#991b1b', icon: '🔴' }
    };
    const riskLevel = results?.summary?.risk_level || 'MEDIUM';
    const colors = riskColors[riskLevel] || riskColors.MEDIUM;

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Prior Art Deep Dive: ${formData?.title || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 900px; margin: 0 auto; padding: 20px; line-height: 1.6; }
    h1 { color: #1e293b; border-bottom: 3px solid #0369a1; padding-bottom: 10px; }
    h2 { color: #0369a1; margin-top: 30px; }
    .risk-box { background: ${colors.bg}; border-left: 4px solid ${colors.border}; padding: 20px; margin: 20px 0; border-radius: 8px; }
    .risk-label { font-size: 24px; font-weight: bold; color: ${colors.text}; }
    .risk-headline { color: ${colors.text}; margin-top: 10px; }
    .stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin: 20px 0; }
    .stat-box { background: #f8fafc; padding: 20px; border-radius: 8px; text-align: center; }
    .stat-number { font-size: 28px; font-weight: bold; color: #0369a1; }
    .stat-label { font-size: 12px; color: #64748b; margin-top: 5px; }
    .invention-details { background: #f0f9ff; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .invention-details p { margin: 10px 0; }
    .summary-box { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .findings-section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .findings-section ul { margin: 10px 0; padding-left: 25px; }
    .findings-section li { margin: 10px 0; }
    .patent-card { background: white; border: 1px solid #e2e8f0; padding: 15px; border-radius: 8px; margin: 15px 0; }
    .patent-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
    .patent-number { font-weight: bold; color: #0369a1; }
    .relevance-score { background: #0369a1; color: white; padding: 4px 12px; border-radius: 12px; font-size: 14px; }
    .relevance-high { background: #dc2626; }
    .relevance-medium { background: #d97706; }
    .relevance-low { background: #16a34a; }
    .recommendations-section { background: #dcfce7; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .recommendations-section h3 { color: #166534; margin-top: 0; }
    .recommendations-section ol { margin: 10px 0; padding-left: 25px; }
    .recommendations-section li { margin: 10px 0; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Prior Art Deep Dive Report</h1>

  <div class="risk-box">
    <div class="risk-label">${colors.icon} ${riskLevel === 'LOW' ? 'Limited Prior Art Found' : riskLevel === 'HIGH' ? 'Significant Prior Art Found' : 'Moderate Prior Art Found'}</div>
    <div class="risk-headline">${results?.summary?.headline || ''}</div>
  </div>

  <div class="stats-grid">
    <div class="stat-box">
      <div class="stat-number">${(results?.prior_art || []).length}</div>
      <div class="stat-label">Relevant Patents Found</div>
    </div>
    <div class="stat-box">
      <div class="stat-number">${results?.total_searched || 0}</div>
      <div class="stat-label">Patents Searched</div>
    </div>
  </div>

  <h2>Invention Details</h2>
  <div class="invention-details">
    <p><strong>Title:</strong> ${formData?.title || 'Not specified'}</p>
    <p><strong>Description:</strong> ${formData?.description || 'Not specified'}</p>
    <p><strong>Key Features:</strong> ${formData?.keyFeatures || 'Not specified'}</p>
    <p><strong>Technical Field:</strong> ${formData?.field || 'Not specified'}</p>
    ${formData?.priorityDate ? `<p><strong>Priority Date:</strong> ${formData.priorityDate}</p>` : ''}
  </div>

  <h2>Executive Summary</h2>
  <div class="summary-box">
    <p>${results?.summary?.executive_summary || 'No summary available'}</p>
  </div>

  ${results?.summary?.key_findings && results.summary.key_findings.length > 0 ? `
    <h2>Key Findings</h2>
    <div class="findings-section">
      <ul>
        ${results.summary.key_findings.map(finding => `<li>📌 ${finding}</li>`).join('')}
      </ul>
    </div>
  ` : ''}

  ${results?.prior_art && results.prior_art.length > 0 ? `
    <h2>Prior Art References (${results.prior_art.length})</h2>
    ${results.prior_art.map(p => {
      const scoreClass = p.relevance_score >= 70 ? 'relevance-high' : p.relevance_score >= 40 ? 'relevance-medium' : 'relevance-low';
      return `
      <div class="patent-card">
        <div class="patent-header">
          <span class="patent-number">${p.patent_number}: ${p.title}</span>
          <span class="relevance-score ${scoreClass}">${p.relevance_score}% Relevant</span>
        </div>
        <p><strong>Assignee:</strong> ${p.assignee || 'Unknown'} | <strong>Grant Date:</strong> ${p.grant_date || 'N/A'}</p>
        <p>${p.relevance_reason || ''}</p>
      </div>
    `}).join('')}
  ` : '<h2>Prior Art References</h2><p>No highly relevant prior art found in initial search.</p>'}

  <div class="recommendations-section">
    <h3>Recommendations</h3>
    <ol>
      ${(results?.summary?.recommendations || []).map(rec => `<li>${rec}</li>`).join('') || '<li>Conduct additional professional prior art search</li>'}
    </ol>
  </div>

  <div class="footer">
    <p><strong>Disclaimer:</strong> This is an automated prior art search for informational purposes only. It is not exhaustive and should not replace a professional patentability search. Consult a patent attorney for legal advice.</p>
    <p>Generated by PatTrak - ${new Date().toLocaleDateString()}</p>
  </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PatTrak_PriorArt_${(formData?.title || 'report').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download as PDF
  const downloadPDF = async () => {
    if (!results) return;
    const riskLevel = results?.summary?.risk_level || 'MEDIUM';
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Prior Art Deep Dive: ${formData?.title || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
    h1 { color: #1e293b; font-size: 24px; }
    h2 { color: #0369a1; font-size: 18px; border-bottom: 2px solid #0369a1; padding-bottom: 8px; margin-top: 30px; }
    .section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .risk-box { padding: 15px; border-radius: 8px; margin: 15px 0; }
    .risk-low { background: #dcfce7; border-left: 4px solid #16a34a; }
    .risk-medium { background: #fef3c7; border-left: 4px solid #d97706; }
    .risk-high { background: #fee2e2; border-left: 4px solid #dc2626; }
    ul, ol { margin: 10px 0; padding-left: 20px; }
    li { margin: 8px 0; }
    .patent-ref { background: white; border: 1px solid #e2e8f0; padding: 12px; margin: 10px 0; border-radius: 6px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Prior Art Deep Dive Report</h1>
  <p><strong>Invention:</strong> ${formData?.title || 'Not specified'}</p>
  <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>
  
  <div class="risk-box risk-${riskLevel.toLowerCase()}">
    <strong>Risk Level: ${riskLevel}</strong>
    <p>${results?.summary?.headline || ''}</p>
  </div>

  <h2>Executive Summary</h2>
  <div class="section">${results?.summary?.executive_summary || ''}</div>

  <h2>Key Findings</h2>
  <div class="section"><ul>${(results?.summary?.key_findings || []).map(item => '<li>' + item + '</li>').join('') || '<li>No findings</li>'}</ul></div>

  ${results?.prior_art && results.prior_art.length > 0 ? '<h2>Prior Art References (' + results.prior_art.length + ')</h2>' + results.prior_art.map(p => '<div class="patent-ref"><strong>' + p.patent_number + ':</strong> ' + p.title + ' (' + p.relevance_score + '% relevant)<br/><small>Assignee: ' + (p.assignee || 'Unknown') + '</small><p>' + (p.relevance_reason || '') + '</p></div>').join('') : ''}

  <h2>Recommendations</h2>
  <div class="section"><ol>${(results?.summary?.recommendations || []).map(item => '<li>' + item + '</li>').join('') || '<li>Conduct additional search</li>'}</ol></div>

  <div class="footer">Generated by PatTrak - Patent Intelligence Made Simple</div>
</body>
</html>`;
    try {
      const response = await fetch(`${config.API_URL}/api/generate-invention-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_html: htmlContent,
          title: formData?.title || 'Prior_Art'
        })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PatTrak_PriorArt_${(formData?.title || 'Prior_Art').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF error:', error);
      alert('Failed to generate PDF');
    }
  };

  // Tier gate - only Premium and Pro users can access
  if (!user || user.tier === 'free') {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <header style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1', fontSize: '14px', fontWeight: '500' }}>
            ← Back
          </button>
        </header>
        
        <main style={{ maxWidth: '600px', margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '48px 32px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔒</div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
              Prior Art Deep Dive
            </h1>
            <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
              Find and analyze existing patents that relate to your invention.
            </p>
            

                {/* Academic Papers Section */}
                {results && results.papers && results.papers.length > 0 && (
                  <div style={{background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '24px'}}>
                    <h3 style={{fontSize: '18px', fontWeight: '600', marginBottom: '16px'}}>Academic Papers ({results.papers_count || results.papers.length})</h3>
                    <p style={{color: '#64748b', marginBottom: '16px', fontSize: '14px'}}>Non-patent literature from OpenAlex, Semantic Scholar, and Crossref</p>
                    {results.papers.map((paper, idx) => {
                      const statusColors = {
                        'BEFORE_FILING': { bg: '#dcfce7', text: '#166534', label: 'Before Filing' },
                        'AFTER_FILING': { bg: '#fee2e2', text: '#991b1b', label: 'After Filing' },
                        'DATE_UNCERTAIN': { bg: '#fef3c7', text: '#92400e', label: 'Date Uncertain' }
                      };
                      const status = statusColors[paper.publication_status] || statusColors['DATE_UNCERTAIN'];
                      return (
                        <div key={idx} style={{border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '12px', background: '#fafafa'}}>
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px'}}>
                            <h4 style={{fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: 0, flex: 1, paddingRight: '12px'}}>{paper.title}</h4>
                            <span style={{backgroundColor: status.bg, color: status.text, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap'}}>{status.label}</span>
                          </div>
                          <div style={{fontSize: '13px', color: '#64748b', marginBottom: '8px'}}>
                            {paper.venue && <span>{paper.venue} - </span>}
                            {paper.publication_year && <span>{paper.publication_year} - </span>}
                            <span>{paper.cited_by_count || 0} citations</span>
                          </div>
                          {paper.authors && paper.authors.length > 0 && (
                            <div style={{fontSize: '12px', color: '#94a3b8', marginBottom: '8px'}}>
                              {paper.authors.slice(0, 3).map(a => a.name).join(', ')}{paper.authors.length > 3 && ' et al.'}
                            </div>
                          )}
                          {paper.why_included && (
                            <div style={{fontSize: '13px', color: '#475569', marginBottom: '8px', fontStyle: 'italic'}}>Why included: {paper.why_included}</div>
                          )}
                          <div style={{display: 'flex', gap: '12px', marginTop: '8px'}}>
                            {paper.doi && <a href={'https://doi.org/' + paper.doi} target="_blank" rel="noopener noreferrer" style={{fontSize: '12px', color: '#0369a1', textDecoration: 'none'}}>View Paper</a>}
                            {paper.pdf_url && <a href={paper.pdf_url} target="_blank" rel="noopener noreferrer" style={{fontSize: '12px', color: '#0369a1', textDecoration: 'none'}}>PDF</a>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', padding: '20px', marginBottom: '32px' }}>
              <p style={{ color: '#0369a1', fontSize: '14px', margin: 0 }}>
                {!user 
                  ? 'Please register or log in to access this tool.'
                  : 'This tool requires a Premium or Pro subscription.'
                }
              </p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '280px', margin: '0 auto' }}>
              {!user ? (
                <>
                  <button onClick={() => setPage('register')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Register Free
                  </button>
                  <button onClick={() => setPage('login')} style={{ padding: '14px 28px', background: 'white', color: '#0369a1', border: '1px solid #0369a1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Log In
                  </button>
                </>
              ) : (
                <button onClick={() => setPage('pricing')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                  Upgrade to Premium
                </button>
              )}
              <button onClick={onBack} style={{ padding: '10px', background: 'none', color: '#64748b', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
                Maybe Later
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#0369a1',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: 0,
            marginBottom: '16px'
          }}
        >
          ← Back to Search
        </button>

        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
          Prior Art Deep Dive
        </h1>
        <p style={{ color: '#64748b', marginTop: '8px', fontSize: '16px' }}>
          Find existing patents and publications that relate to your invention
        </p>
      </div>

      {/* Progress Steps */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '32px',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px'
      }}>
        {[
          { num: 1, label: 'Describe Invention' },
          { num: 2, label: 'Deep Search' },
          { num: 3, label: 'Prior Art Found' }
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: step >= s.num ? '#0369a1' : '#e2e8f0',
                color: step >= s.num ? 'white' : '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {step > s.num ? '✓' : s.num}
              </div>
              <span style={{
                color: step >= s.num ? '#0369a1' : '#64748b',
                fontWeight: step === s.num ? '600' : '400',
                fontSize: '14px'
              }}>
                {s.label}
              </span>
            </div>
            {idx < 2 && (
              <div style={{
                flex: 1,
                height: '2px',
                background: step > s.num ? '#0369a1' : '#e2e8f0',
                margin: '0 16px'
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '24px'
        }}>
          {error}
        </div>
      )}

      {/* Step 1: Input Form */}
      {step === 1 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <div style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#1e40af', fontSize: '14px', fontWeight: '600' }}>
              What is Prior Art?
            </h3>
            <p style={{ margin: 0, color: '#1e40af', fontSize: '14px', lineHeight: '1.5' }}>
              Prior art includes any patents, publications, or public disclosures that existed before your 
              invention's priority date. Finding prior art helps you understand the landscape and strengthen 
              your patent application by distinguishing your invention from what already exists.
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
              Invention Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Self-cleaning solar panel coating"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
              Detailed Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe your invention in detail. Include the problem it solves, how it works, and what makes it different from existing solutions..."
              rows={5}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
              Key Technical Features
            </label>
            <textarea
              value={formData.keyFeatures}
              onChange={(e) => handleInputChange('keyFeatures', e.target.value)}
              placeholder="List the key features that you believe are novel (one per line):&#10;• Feature 1&#10;• Feature 2&#10;• Feature 3"
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                Technical Field <span style={{ color: '#94a3b8', fontWeight: 'normal' }}>(helps find relevant patents)</span>
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={formData.field}
                  onChange={(e) => handleInputChange('field', e.target.value)}
                  placeholder="e.g., Photovoltaic systems, Coatings"
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={suggestFields}
                  disabled={suggestingField || (!formData.title && !formData.description)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid #0369a1',
                    background: suggestingField ? '#e0f2fe' : 'white',
                    color: '#0369a1',
                    cursor: suggestingField || (!formData.title && !formData.description) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {suggestingField ? 'Thinking...' : 'Suggest'}
                </button>
              </div>
              {suggestedFields.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {suggestedFields.map((field, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => { handleInputChange('field', field); setSuggestedFields([]); }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        border: '1px solid #0369a1',
                        background: '#f0f9ff',
                        color: '#0369a1',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {field}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151' }}>
                Priority Date
              </label>
              <input
                type="date"
                value={formData.priorityDate}
                onChange={(e) => handleInputChange('priorityDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '15px',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                Only show prior art before this date
              </p>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            style={{
              width: '100%',
              padding: '14px',
              background: '#0369a1',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Search for Prior Art →
          </button>
        </div>
      )}

      {/* Step 2: Loading */}
      {step === 2 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '48px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            border: '4px solid #e2e8f0',
            borderTopColor: '#0369a1',
            borderRadius: '50%',
            margin: '0 auto 24px',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          
          <h2 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>Deep Prior Art Search</h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>{progress.message}</p>
          
          <div style={{
            background: '#f1f5f9',
            borderRadius: '8px',
            height: '8px',
            overflow: 'hidden',
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            <div style={{
              background: '#0369a1',
              height: '100%',
              width: `${progress.percent}%`,
              transition: 'width 0.5s ease'
            }} />
          </div>
          <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '12px' }}>{progress.percent}% complete</p>

          <div style={{
            background: '#f8fafc',
            borderRadius: '8px',
            padding: '16px',
            marginTop: '24px',
            textAlign: 'left',
            maxWidth: '500px',
            margin: '24px auto 0'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
              <strong>What's happening:</strong> We're using AI semantic search to find patents 
              with similar technical concepts, not just keyword matches. This finds relevant 
              prior art that traditional searches might miss.
            </p>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && results && (
        <div>
          {/* Summary Banner */}
          <div style={{
            background: results.summary?.risk_level === 'HIGH' ? '#fef2f2' : 
                       results.summary?.risk_level === 'MEDIUM' ? '#fef3c7' : '#dcfce7',
            border: `2px solid ${results.summary?.risk_level === 'HIGH' ? '#dc2626' : 
                                results.summary?.risk_level === 'MEDIUM' ? '#f59e0b' : '#16a34a'}`,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ 
                  margin: '0 0 8px 0', 
                  color: results.summary?.risk_level === 'HIGH' ? '#dc2626' : 
                         results.summary?.risk_level === 'MEDIUM' ? '#92400e' : '#166534',
                  fontSize: '24px'
                }}>
                  {results.summary?.risk_level === 'HIGH' ? 'Significant Prior Art Found' :
                   results.summary?.risk_level === 'MEDIUM' ? 'Moderate Prior Art Found' :
                   'Limited Prior Art Found'}
                </h2>
                <p style={{ 
                  margin: 0, 
                  color: results.summary?.risk_level === 'HIGH' ? '#dc2626' : 
                         results.summary?.risk_level === 'MEDIUM' ? '#92400e' : '#166534',
                  fontSize: '16px'
                }}>
                  {results.summary?.headline}
                </p>
              </div>
              <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '12px 20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b' }}>
                  {results.prior_art?.length || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Patents Found</div>
              </div>
            </div>
          </div>

          {/* Executive Summary */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Executive Summary</h3>
            <p style={{ margin: 0, color: '#475569', lineHeight: '1.6' }}>
              {results.summary?.executive_summary}
            </p>
          </div>

          {/* Key Findings */}
          {results.summary?.key_findings && (
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Key Findings</h3>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569' }}>
                {results.summary.key_findings.map((finding, idx) => (
                  <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.5' }}>{finding}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Prior Art List */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>
              Prior Art References ({results.prior_art?.length || 0})
            </h3>
            
            {results.prior_art?.map((patent, idx) => {
              const colors = getRiskColor(patent.relevance_score || 50);
              return (
                <div key={idx} style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '12px',
                  background: colors.bg
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <a 
                        href={`https://patents.google.com/patent/US${patent.patent_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          color: '#0369a1', 
                          textDecoration: 'none',
                          fontWeight: '600',
                          fontSize: '15px'
                        }}
                      >
                        US{patent.patent_number}
                      </a>
                      <span style={{ color: '#64748b', marginLeft: '12px', fontSize: '14px' }}>
                        {patent.grant_date || patent.filing_date}
                      </span>
                    </div>
                    <span style={{
                      background: colors.border,
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {patent.relevance_score}% Match
                    </span>
                  </div>
                  <h4 style={{ margin: '0 0 8px 0', color: '#1e293b', fontSize: '15px' }}>
                    {patent.title}
                  </h4>
                  <p style={{ margin: '0 0 8px 0', color: '#475569', fontSize: '14px', lineHeight: '1.5' }}>
                    {patent.relevance_reason}
                  </p>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    <strong>Assignee:</strong> {patent.assignee || 'Not specified'}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recommendations */}
          {results.summary?.recommendations && (
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Recommendations</h3>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569' }}>
                {results.summary.recommendations.map((rec, idx) => (
                  <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.5' }}>{rec}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Academic Papers Section */}
          {results && results.papers && results.papers.length > 0 && (
            <div style={{background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '24px'}}>
              <h3 style={{fontSize: '18px', fontWeight: '600', marginBottom: '16px'}}>Academic Papers ({results.papers_count || results.papers.length})</h3>
              <p style={{color: '#64748b', marginBottom: '16px', fontSize: '14px'}}>Non-patent literature from OpenAlex, Semantic Scholar, and Crossref</p>
              {results.papers.map((paper, idx) => {
                const statusColors = {
                  'BEFORE_FILING': { bg: '#dcfce7', text: '#166534', label: 'Before Filing' },
                  'AFTER_FILING': { bg: '#fee2e2', text: '#991b1b', label: 'After Filing' },
                  'DATE_UNCERTAIN': { bg: '#fef3c7', text: '#92400e', label: 'Date Uncertain' }
                };
                const status = statusColors[paper.publication_status] || statusColors['DATE_UNCERTAIN'];
                return (
                  <div key={idx} style={{border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '12px', background: '#fafafa'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px'}}>
                      <h4 style={{fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: 0, flex: 1, paddingRight: '12px'}}>{paper.title}</h4>
                      <span style={{backgroundColor: status.bg, color: status.text, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap'}}>{status.label}</span>
                    </div>
                    <div style={{fontSize: '13px', color: '#64748b', marginBottom: '8px'}}>
                      {paper.venue && <span>{paper.venue} - </span>}
                      {paper.publication_year && <span>{paper.publication_year} - </span>}
                      <span>{paper.cited_by_count || 0} citations</span>
                    </div>
                    {paper.authors && paper.authors.length > 0 && (
                      <div style={{fontSize: '12px', color: '#94a3b8', marginBottom: '8px'}}>
                        {paper.authors.slice(0, 3).map(a => a.name).join(', ')}{paper.authors.length > 3 && ' et al.'}
                      </div>
                    )}
                    {paper.why_included && (
                      <div style={{fontSize: '13px', color: '#475569', marginBottom: '8px', fontStyle: 'italic'}}>Why included: {paper.why_included}</div>
                    )}
                    <div style={{display: 'flex', gap: '12px', marginTop: '8px'}}>
                      {paper.doi && <a href={'https://doi.org/' + paper.doi} target="_blank" rel="noopener noreferrer" style={{fontSize: '12px', color: '#0369a1', textDecoration: 'none'}}>View Paper</a>}
                      {paper.pdf_url && <a href={paper.pdf_url} target="_blank" rel="noopener noreferrer" style={{fontSize: '12px', color: '#0369a1', textDecoration: 'none'}}>PDF</a>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* What You Learned */}
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#0369a1' }}>What You Learned</h3>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#0c4a6e' }}>
              <li style={{ marginBottom: '6px' }}>How to identify relevant prior art for your invention</li>
              <li style={{ marginBottom: '6px' }}>Which existing patents are most similar to your concept</li>
              <li style={{ marginBottom: '6px' }}>How to distinguish your invention from prior art</li>
              <li>What to discuss with a patent attorney</li>
            </ul>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={resetJourney}
              style={{
                padding: '12px 24px',
                background: 'white',
                color: '#0369a1',
                border: '1px solid #0369a1',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ← Start New Search
            </button>
            <button
              onClick={saveReport}
              disabled={saving}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: saveSuccess ? '#16a34a' : '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Report'}
            </button>
            <button
              onClick={downloadHTML}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download HTML
            </button>
            <button
              onClick={downloadPDF}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriorArtDeepDivePage;
